import React, { useEffect, useState, useContext, useMemo, useCallback, useRef } from "react";
import { withRouter } from "react-router-dom";
import { StoreContext } from "../../store/store";
import Loader from "../Loader";
import UserAvatar from "../UserAvatar";
import { useLocalization } from "../../hooks/useLocalization";
import "./style.scss";
import { useGetSurveys, useSetMainSurvey } from "../../client-api/survey";
import { PREFILL_FIELDS } from '../CarEdit/survey-constant';

const SCREEN_TEXT = [
  "愛車一覧", // Car List
  "愛車を登録", // Register Car
  "愛車名 (ニックネーム)", // Car Name (Nickname)
  "メーカー・車種・年式", // Make・Model・Year
  "愛車が登録されていません", // No cars registered
];

// Memoized Car Card Component
const CarCard = React.memo(({ car, mainItem, currentUser, onEditCar, onMainSelected }) => {
  const carImage = car.icon || car.imageUrl;
  const carName = car.name;
  const carModel = car.model;
  const carManufacturer = car.manufacturer;
  const carYear = new Date(car.yearType).getFullYear();

  return (
    <div className="car-card">
      <div className="car-card-header">
        <UserAvatar user={currentUser} size={32} />
        <div className="car-card-header-text">
          <div className="car-card-title">{carName}</div>
          <div
            onClick={(e) => {
              e.stopPropagation();
              onMainSelected();
            }}
            className="car-selection-tag"
            style={{
              backgroundColor: mainItem ? '#AD3D39' : 'transparent',
              color: mainItem ? 'white' : 'rgba(173, 61, 57, 0.6)',
              borderColor: mainItem ? '#AD3D39' : 'rgba(173, 61, 57, 0.6)',
            }}
          >
            {mainItem ? (
              <img src="/loyality_white.png" alt="Main-Car" width="18" height="18" />
            ) : (
              <img src="/loyality_green.png" alt="Main-Car" width="18" height="18" />
            )}
            <p>メイン</p>
          </div>
        </div>
        <div
          className="car-card-edit-icon"
          onClick={(e) => {
            e.stopPropagation();
            onEditCar(car);
          }}
        >
          <img src="/pencil-outline.svg" alt="Edit" width="24" height="24" />
        </div>
      </div>
      <div className="car-card-body">
        <div className="car-card-image-wrapper">
          {carImage ? (
            <img src={carImage} alt={carName} className="car-card-image" />
          ) : (
            <div className="car-card-image-placeholder">
              <span>No Image</span>
            </div>
          )}
        </div>
      </div>
      <div className="car-card-footer">
        <p>メーカー：{carManufacturer}</p>
        <p>車種：{carModel}</p>
        <p>年式：{carYear}年</p>
      </div>
    </div>
  );
});

CarCard.displayName = "CarCard";

const CarListing = (props) => {
  const { state, actions } = useContext(StoreContext);
  const { user, account } = state;
  const [loading, setLoading] = useState(true);
  const userParam = props.match.params.userid;
  const hasFetchedRef = useRef(false);
  const lastUserParamRef = useRef(null);

  const { translatedTexts: texts } = useLocalization(SCREEN_TEXT);
  const { data: surveys, isLoading: isSurveysLoading } = useGetSurveys(userParam);
  const setMainSurveyMutation = useSetMainSurvey(userParam);

  const carCount = useMemo(() => {
    return Array.isArray(surveys) ? surveys.length : 0;
  }, [surveys]);

  const isCarLimitReached = carCount >= 10;
  const isAddDisabled = isSurveysLoading || isCarLimitReached;

  // Memoize currentUser calculation
  const currentUser = useMemo(() => {
    return account && account._id === userParam ? account : user;
  }, [account, user, userParam]);

  
  const handleEditCar = useCallback((car) => {
    props.history.push({
      pathname: `/settings/${userParam}/cars/form`,
      state: {
        survey: car,
        isEdit: true,
      },
    });
  }, [props.history, userParam]);

  const handleSetMainCar = useCallback((carId) => {
    if (!carId || setMainSurveyMutation.isLoading) return;
    setMainSurveyMutation.mutate(carId);
  }, [setMainSurveyMutation]);

  const normalizedSurveys = useMemo(() => {
    if (!Array.isArray(surveys) || surveys.length === 0) return [];

    const hasExplicitMain = surveys.some((car) => car.isMain === true);

    return surveys.map((car, index) => ({
      ...car,
      isMain: hasExplicitMain ? car.isMain === true : index === 0,
    }));
  }, [surveys]);

  const handleAddCar = useCallback(() => {
    if (isAddDisabled) return;

    const sourceCar = normalizedSurveys.length > 0 ? normalizedSurveys[0] : null;

    const prefillSurvey = sourceCar
      ? PREFILL_FIELDS.reduce((acc, field) => {
        acc[field] = sourceCar[field] ?? "";
        return acc;
      }, {})
      : null;

    props.history.push({
      pathname: `/settings/${userParam}/cars/form`,
      state: { prefillSurvey },
    });
  }, [isAddDisabled, props.history, userParam, normalizedSurveys]);

  // Initialize component and fetch data
  useEffect(() => {
    const currentToken = localStorage.getItem("carvibestoken");
    if (!currentToken || currentToken.length === 0) {
      window.location.href = "/login";
      return;
    }

    // Only fetch if userParam changed (not already fetched for this userParam)
    if (lastUserParamRef.current === userParam) {
      return;
    }

    window.scrollTo(0, 0);
    setLoading(true);
    actions.getUser(userParam);
    lastUserParamRef.current = userParam;

    // Set loading to false after data fetch
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userParam]); // Removed 'actions' from dependencies - it's stable from context

  // Cleanup body styles on unmount
  useEffect(() => {
    return () => {
      document.getElementsByTagName("body")[0].style.cssText = "";
    };
  }, []);

  return (
    <div className="car-listing-root">
      <div className="car-listing-header">
        <h2 className="car-listing-title">{texts[0]}</h2>
        <div className="car-listing-header-actions">
          <button
            className={isCarLimitReached ? "disabled-add-car-button" : "add-car-button"}
            onClick={handleAddCar}
          >
            <span>{texts[1]}</span>
          </button>
        </div>
      </div>
      {loading ? (
        <div className="car-listing-loader-wrapper">
          <Loader />
        </div>
      ) : (
        <div className="car-listing-content">
          {normalizedSurveys.length > 0 ? (
            <div className="car-listing-grid">
              {normalizedSurveys.map((car, index) => (
                <CarCard
                  key={car._id || `car-${index}`}
                  car={car}
                  mainItem={car.isMain}
                  currentUser={currentUser || user}
                  onEditCar={handleEditCar}
                  onMainSelected={() => handleSetMainCar(car._id)}
                />
              ))}
            </div>
          ) : (
            <div className="car-listing-empty">
              <p>{texts[4]}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default withRouter(CarListing);
